import { Menu, Layers, LifeBuoy, Info, Mail, Key, Camera, Flame, Shield, Monitor, Briefcase, Wrench, ChevronDown, Home, Phone, LogIn, BookOpen } from 'lucide-react';
import { Button } from '../ui/button';
import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { MobileMenu } from './mobile-menu';
import { Logo } from '../ui/logo';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { config } from '@/lib/config';
import { cn } from '@/lib/utils';
import { reportPhoneConversion } from '@/utils/analytics';

export function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  // Track scroll position for navbar effects
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Check if current path matches
  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/' || location.pathname === '/home';
    return location.pathname.startsWith(path);
  };

  return (
  <header
    className={cn(
      "sticky top-0 z-40 bg-gradient-to-r from-[#f1c232] via-[#f4c84a] to-[#e69138] backdrop-blur-sm border-b border-[#e69138]/20 transition-all duration-300 overflow-hidden",
      isScrolled ? "shadow-xl" : "shadow-lg"
    )}
    role="banner"
    aria-label="Site header"
  >
      <div className="mx-auto flex h-auto max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8 relative">
        <div className="flex items-center gap-x-2 sm:gap-x-3 md:gap-x-4 lg:gap-x-7 flex-shrink-0 min-w-0">
          <Button
            variant="ghost"
            size="sm"
            className="lg:hidden hover:bg-white/20 transition-all duration-300 hover:scale-110 flex-shrink-0 z-50"
            onClick={() => setIsMobileMenuOpen(true)}
            aria-label="Open mobile menu"
          >
            <Menu className="h-5 w-5 text-white transition-transform duration-300 hover:rotate-90" />
          </Button>
          <Logo variant="light" size="lg" className={cn(
            "w-auto flex-shrink-0 transition-all duration-300",
            isScrolled ? "h-16" : "h-20"
          )} />
          <span className="text-white text-[10px] sm:text-xs md:text-sm lg:text-sm font-normal italic text-left leading-[1.3] whitespace-nowrap sm:whitespace-normal flex-shrink-0 sm:max-w-[200px] md:max-w-[250px] lg:max-w-[360px]">
            <span className="hidden sm:inline">Serving the Greater Bay Area <br /> Since 1986</span>
            <span className="sm:hidden">Since 1986</span>
          </span>
        </div>

  {/* Desktop Navigation */}
  <nav className="hidden lg:flex lg:items-center lg:gap-x-1 xl:gap-x-2 flex-shrink-0 whitespace-nowrap">
          {/* Home shortcut */}
          <Link
            to="/"
            className={cn(
              "group relative inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-300 hover:text-red-700 hover:bg-white/20 hover:shadow-md hover:scale-105 whitespace-nowrap",
              isActive('/')
                ? "text-red-700 bg-white/25 shadow-md font-semibold"
                : "text-gray-900"
            )}
          >
            <Home className="h-4 w-4 flex-shrink-0 transition-transform duration-300 group-hover:scale-110" />
            <span className="relative">
              Home
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-red-700 transition-all duration-300 group-hover:w-full"></span>
            </span>
          </Link>
          {/* Services dropdown */}
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                className={cn(
                  "group relative inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-300 hover:text-red-700 hover:bg-white/20 hover:shadow-md hover:scale-105 focus:outline-none whitespace-nowrap",
                  isActive('/services')
                    ? "text-red-700 bg-white/25 shadow-md font-semibold"
                    : "text-gray-900"
                )}
                aria-label="Services menu"
              >
                <Layers className="h-4 w-4 flex-shrink-0 transition-transform duration-300 group-hover:rotate-12" aria-hidden="true" />
                <span className="relative">
                  Services
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-red-700 transition-all duration-300 group-hover:w-full"></span>
                </span>
                <ChevronDown className="h-4 w-4 opacity-80 flex-shrink-0 transition-transform duration-300 group-hover:rotate-180" aria-hidden="true" />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                sideOffset={8}
                align="start"
                className="min-w-[240px] overflow-hidden rounded-lg border border-gray-200 bg-white/95 backdrop-blur-md p-1 shadow-xl ring-1 ring-black/5 z-50 animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 duration-200"
              >
                {/* Optional: All Services */}
                <DropdownMenu.Item asChild>
                  <Link to="/services" className="group flex items-center gap-2 rounded-md px-2 py-2 text-sm text-gray-900 outline-none hover:bg-[#fef3c7] focus:bg-[#fef3c7] transition-all duration-200 hover:translate-x-1">
                    <Layers className="h-4 w-4 text-red-700 transition-transform duration-200 group-hover:scale-110" aria-hidden="true" />
                    <span>All Services</span>
                  </Link>
                </DropdownMenu.Item>
                <DropdownMenu.Separator className="my-1 h-px bg-gray-200" />
                <DropdownMenu.Item asChild>
                  <Link to="/services/access-control" className="group flex items-center gap-2 rounded-md px-2 py-2 text-sm text-gray-900 outline-none hover:bg-[#fef3c7] focus:bg-[#fef3c7] transition-all duration-200 hover:translate-x-1">
                    <Key className="h-4 w-4 text-red-700 transition-transform duration-200 group-hover:scale-110" aria-hidden="true" />
                    <span>Access Control</span>
                  </Link>
                </DropdownMenu.Item>
                <DropdownMenu.Item asChild>
                  <Link to="/services/cctv-systems" className="group flex items-center gap-2 rounded-md px-2 py-2 text-sm text-gray-900 outline-none hover:bg-[#fef3c7] focus:bg-[#fef3c7] transition-all duration-200 hover:translate-x-1">
                    <Camera className="h-4 w-4 text-red-700 transition-transform duration-200 group-hover:scale-110" aria-hidden="true" />
                    <span>CCTV Systems</span>
                  </Link>
                </DropdownMenu.Item>
                <DropdownMenu.Item asChild>
                  <Link to="/services/fire-alarm" className="group flex items-center gap-2 rounded-md px-2 py-2 text-sm text-gray-900 outline-none hover:bg-[#fef3c7] focus:bg-[#fef3c7] transition-all duration-200 hover:translate-x-1">
                    <Flame className="h-4 w-4 text-red-700 transition-transform duration-200 group-hover:scale-110" aria-hidden="true" />
                    <span>Fire Alarm</span>
                  </Link>
                </DropdownMenu.Item>
                <DropdownMenu.Item asChild>
                  <Link to="/services/burglar-alarms" className="group flex items-center gap-2 rounded-md px-2 py-2 text-sm text-gray-900 outline-none hover:bg-[#fef3c7] focus:bg-[#fef3c7] transition-all duration-200 hover:translate-x-1">
                    <Shield className="h-4 w-4 text-red-700 transition-transform duration-200 group-hover:scale-110" aria-hidden="true" />
                    <span>Burglar Alarms</span>
                  </Link>
                </DropdownMenu.Item>
                <DropdownMenu.Item asChild>
                  <Link to="/services/24-7-monitoring" className="group flex items-center gap-2 rounded-md px-2 py-2 text-sm text-gray-900 outline-none hover:bg-[#fef3c7] focus:bg-[#fef3c7] transition-all duration-200 hover:translate-x-1">
                    <Monitor className="h-4 w-4 text-red-700 transition-transform duration-200 group-hover:scale-110" aria-hidden="true" />
                    <span>24/7 Monitoring</span>
                  </Link>
                </DropdownMenu.Item>
                <DropdownMenu.Item asChild>
                  <Link to="/services/errcs" className="group flex items-center gap-2 rounded-md px-2 py-2 text-sm text-gray-900 outline-none hover:bg-[#fef3c7] focus:bg-[#fef3c7] transition-all duration-200 hover:translate-x-1">
                    <Monitor className="h-4 w-4 text-red-700 transition-transform duration-200 group-hover:scale-110" aria-hidden="true" />
                    <span>ERRCS</span>
                  </Link>
                </DropdownMenu.Item>
                <DropdownMenu.Separator className="my-1 h-px bg-gray-200" />
                <DropdownMenu.Item asChild>
                  <Link to="/services/consulting" className="group flex items-center gap-2 rounded-md px-2 py-2 text-sm text-gray-900 outline-none hover:bg-[#fef3c7] focus:bg-[#fef3c7] transition-all duration-200 hover:translate-x-1">
                    <Briefcase className="h-4 w-4 text-red-700 transition-transform duration-200 group-hover:scale-110" aria-hidden="true" />
                    <span>Consulting</span>
                  </Link>
                </DropdownMenu.Item>
                <DropdownMenu.Item asChild>
                  <Link to="/services/maintenance" className="group flex items-center gap-2 rounded-md px-2 py-2 text-sm text-gray-900 outline-none hover:bg-[#fef3c7] focus:bg-[#fef3c7] transition-all duration-200 hover:translate-x-1">
                    <Wrench className="h-4 w-4 text-red-700 transition-transform duration-200 group-hover:scale-110" aria-hidden="true" />
                    <span>Maintenance</span>
                  </Link>
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
          {/*<Link
            to="/shop"
            className="text-sm font-medium text-gray-300 transition-colors hover:text-[#e69138]"
          >
            Shop
          </Link>*/}
          {/* Support dropdown */}
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                className={cn(
                  "group relative inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-300 hover:text-red-700 hover:bg-white/20 hover:shadow-md hover:scale-105 focus:outline-none whitespace-nowrap",
                  isActive('/support') || isActive('/blog') || isActive('/resources')
                    ? "text-red-700 bg-white/25 shadow-md font-semibold"
                    : "text-gray-900"
                )}
                aria-label="Support menu"
              >
                <LifeBuoy className="h-4 w-4 flex-shrink-0 transition-transform duration-300 group-hover:rotate-12" />
                <span className="relative">
                  Support
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-red-700 transition-all duration-300 group-hover:w-full"></span>
                </span>
                <ChevronDown className="h-4 w-4 opacity-80 flex-shrink-0 transition-transform duration-300 group-hover:rotate-180" />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                sideOffset={8}
                align="start"
                className="min-w-[200px] overflow-hidden rounded-lg border border-gray-200 bg-white/95 backdrop-blur-md p-1 shadow-xl ring-1 ring-black/5 z-50 animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 duration-200"
              >
                <DropdownMenu.Item asChild>
                  <Link to="/support" className="group flex items-center gap-2 rounded-md px-2 py-2 text-sm text-gray-900 outline-none hover:bg-[#fef3c7] focus:bg-[#fef3c7] transition-all duration-200 hover:translate-x-1">
                    <LifeBuoy className="h-4 w-4 text-red-700 transition-transform duration-200 group-hover:scale-110" />
                    <span>FAQ</span>
                  </Link>
                </DropdownMenu.Item>
                <DropdownMenu.Item asChild>
                  <Link to="/blog" className="group flex items-center gap-2 rounded-md px-2 py-2 text-sm text-gray-900 outline-none hover:bg-[#fef3c7] focus:bg-[#fef3c7] transition-all duration-200 hover:translate-x-1">
                    <BookOpen className="h-4 w-4 text-red-700 transition-transform duration-200 group-hover:scale-110" />
                    <span>Blog</span>
                  </Link>
                </DropdownMenu.Item>
                <DropdownMenu.Item asChild>
                  <Link to="/resources" className="group flex items-center gap-2 rounded-md px-2 py-2 text-sm text-gray-900 outline-none hover:bg-[#fef3c7] focus:bg-[#fef3c7] transition-all duration-200 hover:translate-x-1">
                    <BookOpen className="h-4 w-4 text-red-700 transition-transform duration-200 group-hover:scale-110" />
                    <span>Resources</span>
                  </Link>
                </DropdownMenu.Item>
                <DropdownMenu.Item asChild>
                  <Link to="/contact" className="group flex items-center gap-2 rounded-md px-2 py-2 text-sm text-gray-900 outline-none hover:bg-[#fef3c7] focus:bg-[#fef3c7] transition-all duration-200 hover:translate-x-1">
                    <Mail className="h-4 w-4 text-red-700 transition-transform duration-200 group-hover:scale-110" />
                    <span>Contact Support</span>
                  </Link>
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
          <Link
            to="/about"
            className={cn(
              "group relative inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-300 hover:text-red-700 hover:bg-white/20 hover:shadow-md hover:scale-105 whitespace-nowrap",
              isActive('/about')
                ? "text-red-700 bg-white/25 shadow-md font-semibold"
                : "text-gray-900"
            )}
          >
            <Info className="h-4 w-4 flex-shrink-0 transition-transform duration-300 group-hover:scale-110" />
            <span className="relative">
              About Us
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-red-700 transition-all duration-300 group-hover:w-full"></span>
            </span>
          </Link>
          <Link
            to="/blog"
            className={cn(
              "group relative inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-300 hover:text-red-700 hover:bg-white/20 hover:shadow-md hover:scale-105 whitespace-nowrap",
              isActive('/blog')
                ? "text-red-700 bg-white/25 shadow-md font-semibold"
                : "text-gray-900"
            )}
          >
            <BookOpen className="h-4 w-4 flex-shrink-0 transition-transform duration-300 group-hover:scale-110" />
            <span className="relative">
              Blog
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-red-700 transition-all duration-300 group-hover:w-full"></span>
            </span>
          </Link>
          <Link
            to="/resources"
            className={cn(
              "group relative inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-300 hover:text-red-700 hover:bg-white/20 hover:shadow-md hover:scale-105 whitespace-nowrap",
              isActive('/resources')
                ? "text-red-700 bg-white/25 shadow-md font-semibold"
                : "text-gray-900"
            )}
          >
            <BookOpen className="h-4 w-4 flex-shrink-0 transition-transform duration-300 group-hover:scale-110" />
            <span className="relative">
              Resources
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-red-700 transition-all duration-300 group-hover:w-full"></span>
            </span>
          </Link>
          <Link
            to="/contact"
            className={cn(
              "group relative inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-300 hover:text-red-700 hover:bg-white/20 hover:shadow-md hover:scale-105 whitespace-nowrap",
              isActive('/contact')
                ? "text-red-700 bg-white/25 shadow-md font-semibold"
                : "text-gray-900"
            )}
          >
            <Mail className="h-4 w-4 flex-shrink-0 transition-transform duration-300 group-hover:scale-110" />
              <span className="relative">
              Contact
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-red-700 transition-all duration-300 group-hover:w-full"></span>
            </span>
          </Link>
          {/* FieldVero Portal Link - Placeholder */}
          {config.fieldVeroUrl !== '#' ? (
            <a
              href={config.fieldVeroUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-gray-900 transition-all duration-300 hover:text-red-700 hover:bg-white/20 hover:shadow-md hover:scale-105 whitespace-nowrap"
              aria-label="Access FieldVero Portal"
            >
              <LogIn className="h-4 w-4 flex-shrink-0 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12" />
              <span className="relative">
                Portal
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-red-700 transition-all duration-300 group-hover:w-full"></span>
              </span>
            </a>
          ) : (
            <span
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-gray-500 cursor-not-allowed opacity-60 whitespace-nowrap"
              aria-label="Portal (Coming Soon)"
            >
              <LogIn className="h-4 w-4 flex-shrink-0" /> <span>Portal</span>
            </span>
          )}
          {/* Call button */}
          <a
            href="tel:8005402140"
            onClick={(event) => {
              event.preventDefault();
              reportPhoneConversion();
            }}
            className="group relative inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-[#e69138] to-[#d17f2a] px-4 py-2.5 text-sm font-semibold text-white shadow-lg transition-all duration-300 hover:from-[#d17f2a] hover:to-[#c16a1f] hover:shadow-xl hover:scale-110 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e69138] focus-visible:ring-offset-2 whitespace-nowrap before:absolute before:inset-0 before:rounded-lg before:bg-white/20 before:opacity-0 before:transition-opacity before:duration-300 hover:before:opacity-100 animate-pulse"
            aria-label="Call (800) 540-2140"
          >
            <Phone className="h-4 w-4 flex-shrink-0 relative z-10 transition-transform duration-300 group-hover:scale-125 group-hover:rotate-12" />
            <span className="relative z-10">Call</span>
            <span className="absolute inset-0 rounded-lg bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
          </a>

        </nav>

        {/* Mobile Call Button (mobile only) */}
        <a
          href="tel:8005402140"
          onClick={(event) => {
            event.preventDefault();
            reportPhoneConversion();
          }}
          className="lg:hidden inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-[#e69138] to-[#d17f2a] px-3 py-2 text-sm font-semibold text-white shadow-md transition-all duration-300 hover:shadow-lg hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e69138]"
          aria-label="Call (800) 540-2140"
        >
          <Phone className="h-4 w-4" /> Call
        </a>

        {/* Mobile Menu <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate('/client/login')}
          >
            Client Portal
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-white/20 text-white hover:bg-white/10"
            onClick={() => navigate('/partner/login')}
          >
            Partner Portal
          </Button>*/}
        <MobileMenu
          isOpen={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
        />
      </div>
    </header>
  );
}
